import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Stack, Paper, CircularProgress, Chip, Alert, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import { getNetworkTopology } from '../../api/client'
import type { NetworkDataTierSummary } from '../../api/types'

function SummaryChip({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <Chip label={`${count} ${label}`} size="small"
      sx={{ bgcolor: color + '18', color, fontWeight: 700, border: `1px solid ${color}33` }} />
  )
}

// ── Diagram ───────────────────────────────────────────────────────────────────
// One row per environment, same visual grammar reused throughout so the difference
// reads at a glance: a horizontal "path to the public internet" line, with boxes
// (services, or VNet boundaries) sitting on it. A diamond glyph marks an actively
// deployed regional Application Gateway; a circular "FD" glyph marks Azure Front
// Door (a global edge service, not a regional VNet resource — it's drawn on the
// line as a parallel entry point, not something boxes sit "behind" the way they
// sit behind a VNet boundary). A dashed/hollow glyph means the resource exists but
// isn't wired to anything yet.

const INK = '#0f172a'
const MUTED = '#64748b'
const LINE = '#cbd5e1'
const BOX_STROKE = '#cbd5e1'
const GATEWAY = '#d97706'
const FRONTDOOR = '#2563eb'
const CRITICAL = '#dc2626'

function GatewayGlyph({ cx, cy, title }: { cx: number; cy: number; title: string }) {
  return (
    <g>
      <rect x={cx - 9} y={cy - 9} width={18} height={18} rx={3}
        fill="#fef3c7" stroke={GATEWAY} strokeWidth={1.4}
        transform={`rotate(45 ${cx} ${cy})`} />
      <title>{title}</title>
    </g>
  )
}

function FrontDoorGlyph({ cx, cy, active, title }: { cx: number; cy: number; active: boolean; title: string }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={10}
        fill={active ? '#dbeafe' : 'none'}
        stroke={active ? FRONTDOOR : '#94a3b8'} strokeWidth={1.4}
        strokeDasharray={active ? undefined : '3 2'} />
      <text x={cx} y={cy + 3} textAnchor="middle" fontSize={7.5} fontWeight={700}
        fill={active ? FRONTDOOR : '#94a3b8'}>FD</text>
      <title>{title}</title>
    </g>
  )
}

function TopologyDiagram() {
  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox="0 0 1000 460" width="100%" style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label="ACC lives in the Production Azure subscription and has 3 VNets, but only 2 of roughly 30 services are VNet-integrated (for outbound NAT and health checks, not database access) — the rest are directly public. Azure Front Door is deployed and actively fronts ACC's web app and API behind a WAF. UAT has one shared VNet with an Application Gateway subnet that's reserved but not deployed, and its own Front Door profile is live, actively fronting LODWeb, the API, and Admin; a separate shared Application Gateway outside UAT's own VNet additionally fronts the UAT API in parallel - both fronting layers live in shared networking resource groups outside LOD's own. Prod has five separate, unpeered VNet groupings across regions — two of them, fronting Api Service and InstructionSync, sit behind an actively deployed Application Gateway, and Azure Front Door separately and actively fronts the web app and API in parallel, not chained through the gateway. In all three environments, the origin App Services behind these front doors accept direct traffic with no IP restrictions, so every one of these protections can be bypassed by calling the underlying azurewebsites.net hostname directly.">

        {/* ACC row */}
        <text x="20" y="85" fontFamily="ui-monospace, monospace" fontSize="13" fontWeight={700} fill={MUTED}>ACC</text>
        <line x1="140" y1="80" x2="900" y2="80" stroke={LINE} strokeWidth={1.5} />
        <rect x="160" y="50" width="220" height="60" rx="8" fill="#ffffff" stroke={BOX_STROKE} strokeWidth={1.5} />
        <text x="270" y="72" textAnchor="middle" fontSize="13" fontWeight={600} fill={INK}>All ACC services</text>
        <text x="270" y="89" textAnchor="middle" fontSize="10" fill={MUTED}>~30 services, 3 VNets exist</text>
        <text x="270" y="102" textAnchor="middle" fontSize="9" fill={MUTED}>only 2 VNet-integrated (NAT/health)</text>
        <FrontDoorGlyph cx={470} cy={80} active
          title="Azure Front Door Premium (LOD-QA-FrontDoor) — active, fronts LODWeb + API + Insights + static, each behind its own WAF policy" />
        <polygon points="900,80 890,76 890,84" fill="#94a3b8" />
        <text x="906" y="84" fontSize="11" fill={MUTED}>Public Internet</text>
        <text x="160" y="34" fontSize="11" fill={CRITICAL} fontWeight={600}>28 of ~30 services fully public, no VNet at all</text>

        {/* UAT row */}
        <text x="20" y="220" fontFamily="ui-monospace, monospace" fontSize="13" fontWeight={700} fill={MUTED}>UAT</text>
        <line x1="140" y1="215" x2="900" y2="215" stroke={LINE} strokeWidth={1.5} />
        <rect x="170" y="180" width="390" height="70" rx="10" fill="none" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 4" />
        <text x="182" y="196" fontSize="10.5" fill={MUTED}>LODS-UAT-Shared-vNET · Central US</text>
        <rect x="190" y="205" width="150" height="35" rx="6" fill="#ffffff" stroke={BOX_STROKE} strokeWidth={1.5} />
        <text x="265" y="227" textAnchor="middle" fontSize="12" fontWeight={600} fill={INK}>UAT services</text>
        <rect x="360" y="205" width="180" height="35" rx="6" fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="3 3" />
        <text x="450" y="219" textAnchor="middle" fontSize="10" fill="#94a3b8">App Gateway subnet</text>
        <text x="450" y="231" textAnchor="middle" fontSize="9.5" fill="#94a3b8">reserved, unused</text>
        <FrontDoorGlyph cx={650} cy={215} active
          title="Azure Front Door Premium (LOD-UAT-FrontDoor) - live, actively routes LODWeb, the API, and Admin" />
        <polygon points="900,215 890,211 890,219" fill="#94a3b8" />
        <text x="906" y="219" fontSize="11" fill={MUTED}>Public Internet</text>
        <text x="170" y="269" fontSize="11" fill={GATEWAY} fontWeight={600}>
          LOD's own gateway subnet unused — but a separate shared App Gateway elsewhere does front the API
        </text>

        {/* Prod row */}
        <text x="20" y="385" fontFamily="ui-monospace, monospace" fontSize="13" fontWeight={700} fill={MUTED}>PROD</text>
        <text x="150" y="310" fontSize="11" fill={MUTED}>8 VNets · 5 regions · 0 peerings between them</text>
        <line x1="140" y1="380" x2="900" y2="380" stroke={LINE} strokeWidth={1.5} />

        <FrontDoorGlyph cx={115} cy={380} active
          title="Azure Front Door Premium (LOD-Prod-FrontDoor) — active, fronts LODWeb + API + Insights, running in parallel with (not chained through) the Application Gateway below" />

        <rect x="150" y="350" width="110" height="60" rx="8" fill="#ffffff" stroke={BOX_STROKE} strokeWidth={1.5} />
        <text x="205" y="375" textAnchor="middle" fontSize="11.5" fontWeight={600} fill={INK}>SCUS services</text>
        <text x="205" y="390" textAnchor="middle" fontSize="10" fill={MUTED}>10 subnets</text>

        <GatewayGlyph cx={280} cy={380} title="Application Gateway (WAF) — actively deployed, fronts Api Service" />

        <rect x="300" y="350" width="110" height="60" rx="8" fill="#ffffff" stroke={GATEWAY} strokeWidth={1.5} />
        <text x="355" y="375" textAnchor="middle" fontSize="11.5" fontWeight={600} fill={INK}>SCUS perimeter</text>
        <text x="355" y="390" textAnchor="middle" fontSize="10" fill={MUTED}>Api Service</text>

        <GatewayGlyph cx={430} cy={380} title="Application Gateway (WAF) — actively deployed, fronts ExternalInstructionSync" />

        <rect x="450" y="350" width="130" height="60" rx="8" fill="#ffffff" stroke={GATEWAY} strokeWidth={1.5} />
        <text x="515" y="375" textAnchor="middle" fontSize="11" fontWeight={600} fill={INK}>InstructionSync</text>
        <text x="515" y="390" textAnchor="middle" fontSize="10" fill={MUTED}>dedicated VNet</text>

        <rect x="600" y="350" width="100" height="60" rx="8" fill="#ffffff" stroke={BOX_STROKE} strokeWidth={1.5} />
        <text x="650" y="375" textAnchor="middle" fontSize="11.5" fontWeight={600} fill={INK}>CentralUS</text>
        <text x="650" y="390" textAnchor="middle" fontSize="10" fill={MUTED}>AWSHost, etc.</text>

        <rect x="720" y="350" width="160" height="60" rx="8" fill="#ffffff" stroke={BOX_STROKE} strokeWidth={1.5} />
        <text x="800" y="372" textAnchor="middle" fontSize="11.5" fontWeight={600} fill={INK}>Secondary regions</text>
        <text x="800" y="387" textAnchor="middle" fontSize="9.5" fill={MUTED}>EastUS / NCUS / EastUS2</text>
        <text x="800" y="399" textAnchor="middle" fontSize="9.5" fill={MUTED}>3 VNets</text>

        <polygon points="900,380 890,376 890,384" fill="#94a3b8" />
        <text x="906" y="384" fontSize="11" fill={MUTED}>Public Internet</text>
        <text x="150" y="437" fontSize="11" fill={MUTED} fontStyle="italic">
          Boxes don't connect to each other — each VNet is its own island (0 peerings)
        </text>
      </svg>
      <figcaption>
        <Typography variant="caption" color="text.secondary">
          Curated from live Azure network resources (VNets, subnets, gateways, Front Door) — a static snapshot, not
          a live query. The diamond marks an actively-deployed Application Gateway; the circle marks Azure Front
          Door, filled when active and dashed when provisioned but unused. Everywhere else, the line runs straight
          through to the public internet.
        </Typography>
      </figcaption>
    </figure>
  )
}

// ── Findings ──────────────────────────────────────────────────────────────────

const FINDINGS: { severity: 'critical' | 'high' | 'medium'; text: string }[] = [
  {
    severity: 'critical',
    text: "Every origin App Service behind a WAF layer — Front Door or Application Gateway — accepts direct traffic with no IP restrictions ('Allow all'), confirmed in ACC, UAT, and Prod alike. Any of these front doors can be bypassed entirely by calling the *.azurewebsites.net hostname directly, which makes the WAF/gateway layer advisory rather than enforced.",
  },
  {
    severity: 'medium',
    text: "UAT's Azure Front Door profile (LOD-UAT-FrontDoor) is live today, routing LODWeb, the API, and Admin — corrects an earlier reading of it as unprovisioned scaffolding. UAT's own LOD-dedicated Application Gateway subnet is separately still unused, but a separate shared (non-LOD) Application Gateway elsewhere in the UAT subscription also fronts the UAT API in parallel. Both fronting layers live in resource groups outside LOD's own (shared-networking-centralus / shared-uat) — a check scoped only to LOD's own resource group misses them entirely, which is how the earlier miscall happened.",
  },
  {
    severity: 'medium',
    text: "Prod's 8 VNets don't peer with each other, so the ~15 services without a gateway reach each other over the public internet in Prod too — that's parity with ACC/UAT, not a gap.",
  },
  {
    severity: 'medium',
    text: "No App Service in any environment reaches its own SQL server privately — every environment relies on the server's public endpoint plus an IP allow-list. ACC's one private endpoint is the exception, and it serves a third-party ETL tool (Rivery) in an external subscription, not LOD's own traffic.",
  },
  {
    severity: 'medium',
    text: "Every SQL server keeps a blanket 'any Azure IP' firewall rule regardless of environment, but ACC's individually-allow-listed IP count (30 rules) dwarfs UAT's (10) and Prod's (6) — informal admin-access sprawl concentrated in the lowest environment.",
  },
]

const SEVERITY_COLOR: Record<string, string> = { critical: CRITICAL, high: GATEWAY, medium: '#2563eb' }

// ── Data tier ─────────────────────────────────────────────────────────────────

function DataTierCell({ dataTier }: { dataTier: NetworkDataTierSummary | null }) {
  if (!dataTier) return <Typography variant="caption" color="text.secondary">—</Typography>
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 12.5 }}>
        {dataTier.sqlServerName}
      </Typography>
      <Typography variant="caption" color="text.secondary">{dataTier.region}</Typography>
    </Stack>
  )
}

function FirewallCell({ dataTier }: { dataTier: NetworkDataTierSummary | null }) {
  if (!dataTier) return <Typography variant="caption" color="text.secondary">—</Typography>
  const color = dataTier.allowsAnyAzureIp ? GATEWAY : '#16a34a'
  return (
    <Stack spacing={0.5}>
      <Chip label={`Public · ${dataTier.firewallRuleCount} rules`} size="small"
        sx={{ bgcolor: color + '18', color, fontWeight: 600, fontSize: 11, height: 20, alignSelf: 'flex-start' }} />
      {dataTier.allowsAnyAzureIp && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
          includes a blanket "any Azure IP" rule
        </Typography>
      )}
    </Stack>
  )
}

function PrivateLinkCell({ dataTier }: { dataTier: NetworkDataTierSummary | null }) {
  if (!dataTier) return <Typography variant="caption" color="text.secondary">—</Typography>
  if (!dataTier.hasPrivateEndpoint) {
    return <Typography variant="caption" color="text.secondary">none</Typography>
  }
  return (
    <Stack spacing={0.5}>
      <Chip label="Private endpoint" size="small"
        sx={{ bgcolor: FRONTDOOR + '18', color: FRONTDOOR, fontWeight: 600, fontSize: 11, height: 20, alignSelf: 'flex-start' }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
        {dataTier.privateEndpointVnet} ({dataTier.privateEndpointResourceGroup})
      </Typography>
      {dataTier.externalPrivateLinkConsumers.map(c => (
        <Typography key={c} variant="caption" color="text.secondary" sx={{ fontSize: 10.5, fontStyle: 'italic' }}>
          {c}
        </Typography>
      ))}
    </Stack>
  )
}

function GeoReplicaCell({ dataTier }: { dataTier: NetworkDataTierSummary | null }) {
  if (!dataTier) return <Typography variant="caption" color="text.secondary">—</Typography>
  if (!dataTier.hasGeoReplica) return <Typography variant="caption" color="text.secondary">none</Typography>
  return <Typography variant="caption" color="text.secondary">{dataTier.geoReplicaRegion}</Typography>
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function NetworkTopologyPanel() {
  const { data: resp, isLoading, error } = useQuery({
    queryKey: ['network-topology'],
    queryFn: getNetworkTopology,
    staleTime: 5 * 60_000,
  })

  const environments = resp?.data?.environments ?? []
  const totals = environments.reduce(
    (acc, e) => ({
      vnets: acc.vnets + e.vnetCount,
      regions: Math.max(acc.regions, e.regionCount),
      gateways: acc.gateways + e.activeGatewayCount,
    }),
    { vnets: 0, regions: 0, gateways: 0 },
  )

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Network Topology</Typography>
          <Typography variant="caption" color="text.secondary">
            How each environment is actually network-wired — VNets, gateways, Front Door, and the database layer
          </Typography>
        </Box>
        {environments.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <SummaryChip label="VNets total" count={totals.vnets} color="#2563eb" />
            <SummaryChip label="max regions" count={totals.regions} color="#2563eb" />
            <SummaryChip label="active gateways" count={totals.gateways} color={GATEWAY} />
          </Stack>
        )}
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">Failed to load the network topology snapshot.</Alert>}

      {environments.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 2, overflowX: 'auto' }}>
            <TopologyDiagram />
          </Paper>

          <Stack spacing={1} mb={3}>
            {FINDINGS.map((f, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: '3px', bgcolor: SEVERITY_COLOR[f.severity], flexShrink: 0, mt: 0.25 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13.5 }}>
                  {f.text}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle1" fontWeight={700} mb={0.5}>Data Tier</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Each environment's core SQL server — how it's reached, and what's connected to it outside the app fleet
          </Typography>

          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Environment</TableCell>
                    <TableCell>SQL Server</TableCell>
                    <TableCell>Public Access & Firewall</TableCell>
                    <TableCell>Private Link</TableCell>
                    <TableCell>Geo-Replica</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {environments.map(env => (
                    <TableRow key={env.environment} hover>
                      <TableCell sx={{ fontWeight: 600, fontSize: 13.5 }}>{env.environment}</TableCell>
                      <TableCell><DataTierCell dataTier={env.dataTier} /></TableCell>
                      <TableCell><FirewallCell dataTier={env.dataTier} /></TableCell>
                      <TableCell><PrivateLinkCell dataTier={env.dataTier} /></TableCell>
                      <TableCell><GeoReplicaCell dataTier={env.dataTier} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  )
}
